// Design Schedules — P1 (docs/implementation/P1_DESIGN_RECORDS_AND_SCHEDULES.md,
// Phase A framework + Phase B first slices, 2026-09-12).
//
// A schedule is a GENERATED VIEW of canonical design data, never a stored
// copy: this module holds the schedule registry (which schedules exist, their
// columns, units, sort, publication levels, qualifiers) and the pure row
// builders that read the same canonical inputs the takeoff/engines already
// use — TakeoffRooms + TakeoffCriteria (via effectiveCriterion), Basis of
// Design option attributes, calcRoomSystems() effective values, placed
// symbols. One renderer (src/components/DesignScheduleTable.jsx) draws every
// schedule from the { columns, rows, notes } this module produces.
//
// Provenance (architecture §3 "provenance contract"): a cell can carry where
// its value came from — 'room' (an estimator's room-specific value), 'template'
// / 'project' (an inherited default), 'calculated' (an engine), 'override' (an
// explicit estimator override of a calculation), 'bod' (a Basis of Design
// option attribute). The renderer shows these marks at the Internal level
// only; a proposal publishes the effective value alone.
//
// Publication levels: each column declares the levels it is published at
// (omitted = every level). A column visible only to estimators declares
// ["internal"]. Which schedules a REVISION publishes, and which
// qualifications print with them, is the per-revision publication
// configuration (Phase E: src/utils/schedulePublication.js,
// EstimateRevisions.SchedulePublicationJson) — this layer only knows how to
// filter a schedule for a level and which qualification KEYS a schedule
// carries (`qualifications`: an array of scheduleQualifications.js keys, or
// a (ctx, level) => keys function); buildSchedule resolves them.
//
// Identity: room rows are keyed by RoomID and marked by RoomNumber (the
// estimator-facing room mark, db/21). HEPA / light rows are TYPE ROWS PER
// ROOM (one row = one room's filter/fixture selection and its effective
// count) — per-instance marks (HEPA-1, LF-1…) are P1 Phase C and are not
// invented here. Pure functions, no React / DB.

import { effectiveCriterion } from "./takeoffCriteria";
import { calcRoomSystems, parseFootCandles } from "../utils/roomSystemsEngine";
import { roomAreasOf, placedCountFor } from "./roomElements";
import { equipmentHeatGain } from "../utils/equipmentLoads";
import { scenarioDispositionLabel } from "../utils/designScenarios";
import { architectureLabel, distributionTypeLabel, scenarioStatusLabel } from "./systemArchitectures";
import { OPENING_KINDS, openingKindOf, compareOpeningMarks, openingRooms, openingWidthFt, openingHeightFt, parseMark } from "./openings";
import { panelLoadSummary, comparePanels, mainsText, voltageText, phasesOf, isValidPhase, resolveDemandFactor } from "../utils/panelLoads";
import { rollupUtilityDemand } from "../utils/utilityDemand";
import { isElectricalType, utilityLabel, unitLabel, statusLabel, partyLabel, assumptionLabel, proposalBasisLabel, dimensionsText, UTILITY_BY_KEY } from "./utilityTypes";
import { qualificationsFor } from "./scheduleQualifications";
export { ELECTRICAL_PROPOSAL_QUALIFICATION, EQUIPMENT_PROPOSAL_QUALIFICATION } from "./scheduleQualifications";

export const PUBLICATION_LEVELS = [
  { key: "internal", label: "Internal",                 description: "Everything, including calculated vs. override, provenance marks and warnings." },
  { key: "proposal", label: "Proposal",                 description: "Client-facing: effective design values only." },
  { key: "dd",       label: "Design Development / IFP", description: "Design values plus the engineering basis a design team needs." },
  // 'ifc' is deliberately not offered yet (brief: do not build IFC-grade
  // documentation before proposal-level schedules are reliable).
];
export const PUBLICATION_LEVEL_KEYS = PUBLICATION_LEVELS.map(l => l.key);

// Provenance mark shown at the Internal level. Keyed by the `source` a cell
// carries; anything not listed gets no mark.
export const PROVENANCE_MARKS = {
  template:   { mark: "†", label: "room template default" },
  project:    { mark: "‡", label: "project default" },
  override:   { mark: "*", label: "estimator override" },
  calculated: { mark: "",  label: "calculated" },
  bod:        { mark: "",  label: "Basis of Design attribute" },
  room:       { mark: "",  label: "room value" },
};

// ---- formatting ---------------------------------------------------------------------

const isBlank = (v) => v === null || v === undefined || v === "" || (typeof v === "number" && !Number.isFinite(v));
// null / undefined / "" are NOT zero (Number(null) === 0) — a blank stays blank.
const num = (v) => { if (isBlank(v)) return null; const n = Number(v); return Number.isFinite(n) ? n : null; };

// Format one cell for display. Every format renders a blank value as "—", so
// a schedule never shows "null", "NaN" or an empty cell.
export function formatCell(col, value) {
  if (isBlank(value)) return "—";
  switch (col.format) {
    case "int":  { const n = num(value); return n == null ? String(value) : Math.round(n).toLocaleString("en-US"); }
    case "num1": { const n = num(value); return n == null ? String(value) : n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 1 }); }
    case "num2": { const n = num(value); return n == null ? String(value) : n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 }); }
    case "pct":  { const n = num(value); return n == null ? String(value) : `${n.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`; }
    case "list": return Array.isArray(value) ? (value.length ? value.join("; ") : "—") : String(value);
    default:     return String(value);
  }
}

// Natural sort of room marks: numeric prefix first ("2" < "10" < "101" <
// "101A"), then the text; a room with no mark sorts after marked rooms by its
// schedule DisplayOrder, then RoomID — so inserting or renumbering one room
// never reorders unrelated rows arbitrarily.
export function compareRoomMarks(a, b) {
  const ma = String(a.RoomNumber ?? "").trim(), mb = String(b.RoomNumber ?? "").trim();
  if (ma && !mb) return -1;
  if (!ma && mb) return 1;
  if (ma && mb) {
    const na = ma.match(/^\d+/), nb = mb.match(/^\d+/);
    if (na && nb && +na[0] !== +nb[0]) return +na[0] - +nb[0];
    if (na && !nb) return -1;
    if (!na && nb) return 1;
    const c = ma.localeCompare(mb, "en", { numeric: true, sensitivity: "base" });
    if (c !== 0) return c;
  }
  const d = (num(a.DisplayOrder) ?? 0) - (num(b.DisplayOrder) ?? 0);
  return d !== 0 ? d : (num(a.RoomID) ?? 0) - (num(b.RoomID) ?? 0);
}

// ---- shared context helpers -----------------------------------------------------------

// ctx = {
//   rooms            TakeoffRooms rows (with room._templateDefaults attached, as every page does)
//   criteria         criterionKey -> value map
//   zones            TakeoffAHUZones rows
//   classifications  CleanroomClassifications rows
//   roomTemplates    RoomTemplates rows
//   systemsCtx       { bodAttrs, settings, tunables, roomElementsByRoom } for calcRoomSystems
//   takeoffConditions placed TakeoffConditionsDrawn rows (placed counts)
//   equipment        ProcessEquipment rows (sp_GetEquipmentRegister Table1) — P2-D
//   connections      EquipmentConnections rows (Table2)
//   (openings read takeoffConditions + rooms + systemsCtx.tunables — P1 Phase C)
//   panels           EstimatePanels rows (sp_GetEquipmentRegister Table3) — P1 Phase D
//   circuits         EstimateCircuits rows (Table4)
//   utilitySystems   UtilitySystems rows (sp_GetUtilitySystems) — Process Utilities Phase F
// }
// A schedule's `columns` is an array, or a (ctx) => array function for a
// schedule whose columns depend on the data (the Equipment Utility Matrix has
// one column per utility type present) — columnsOf() resolves either.
const zoneName = (ctx, id) => ctx.zones?.find(z => z.AHUZoneID === id)?.ZoneName ?? null;
const templateName = (ctx, id) => ctx.roomTemplates?.find(t => t.RoomTemplateID === id)?.Name ?? null;
function classificationLabel(ctx, id) {
  const c = ctx.classifications?.find(x => x.ClassificationID === id);
  if (!c) return null;
  return c.FDAGrade || c.ISPEGrade || c.EUGrade || null;
}
const crit = (ctx, disc, key, room) => effectiveCriterion(ctx.criteria || {}, disc, key, room);
// Cell with provenance: { value, source } — source follows effectiveCriterion
// ('override' = the room's own row, shown as 'room' here since it is a plain
// room value rather than an override of a calculation).
function critCell(ctx, disc, key, room) {
  const e = crit(ctx, disc, key, room);
  if (e.value === "" || e.value == null) return { value: null, source: null };
  return { value: e.value, source: e.source === "override" ? "room" : e.source };
}
const critNum = (ctx, disc, key, room) => { const c = critCell(ctx, disc, key, room); return { ...c, value: c.value == null ? null : num(c.value) }; };
function systemsFor(ctx, room) {
  if (ctx.systemsByRoom?.has?.(room.RoomID)) return ctx.systemsByRoom.get(room.RoomID);
  return calcRoomSystems(room, ctx.criteria || {}, ctx.systemsCtx || {});
}
const sortedRooms = (ctx) => [...(ctx.rooms || [])].sort(compareRoomMarks);
const cell = (value, source = null) => ({ value, source });
// Round to `d` decimals and drop trailing zeros ("7.7", "12", "0.25") for inline load text.
const r = (v, d = 1) => Number(Number(v).toFixed(d)).toLocaleString("en-US", { maximumFractionDigits: d });

// ---- schedule definitions ---------------------------------------------------------------

const roomCriteriaSchedule = {
  key: "room_criteria",
  title: "Room Design Criteria Schedule",
  domain: "rooms",
  source: "TakeoffRooms + TakeoffCriteria (room value → room template default → project default), CleanroomClassifications, TakeoffAHUZones",
  defaultSort: "room mark",
  levels: ["internal", "proposal", "dd"],
  columns: [
    { key: "mark",        label: "#",               format: "text", align: "left" },
    { key: "room",        label: "Room",            format: "text", align: "left" },
    { key: "template",    label: "Room type",       format: "text", align: "left" },
    { key: "classification", label: "Classification", format: "text", align: "left" },
    { key: "areaSf",      label: "Area",            unit: "SF",   format: "int" },
    { key: "lengthFt",    label: "L",               unit: "ft",   format: "num1", levels: ["internal", "dd"] },
    { key: "widthFt",     label: "W",               unit: "ft",   format: "num1", levels: ["internal", "dd"] },
    { key: "heightFt",    label: "Ceiling ht",      unit: "ft",   format: "num1" },
    { key: "ach",         label: "ACH",             unit: "AC/hr", format: "num1" },
    { key: "supplyCfm",   label: "Supply air",      unit: "CFM",  format: "int" },
    { key: "pressure",    label: "Pressure",        format: "text" },
    { key: "ahu",         label: "AHU",             format: "text", align: "left" },
    { key: "airDelivery", label: "Air delivery",    format: "text", align: "left" },
    { key: "filterType",  label: "Terminal filtration", format: "text", align: "left" },
    { key: "indoorTemp",  label: "Temp",            unit: "°F",   format: "num1" },
    { key: "indoorRh",    label: "RH",              unit: "%",    format: "num1" },
    { key: "lightLevel",  label: "Light level",     format: "text" },
    { key: "fixtureType", label: "Lighting",        format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "occupancy",   label: "Occupancy",       unit: "people", format: "int", levels: ["internal", "dd"] },
    { key: "exhaustCfm",  label: "Process exhaust", unit: "CFM",  format: "int", levels: ["internal", "dd"] },
  ],
  rows(ctx) {
    return sortedRooms(ctx).map(room => {
      const areas = roomAreasOf(room);
      const sys = systemsFor(ctx, room);
      const dims = +room.LengthFt > 0 && +room.WidthFt > 0;
      return {
        id: room.RoomID,
        mark: cell(room.RoomNumber || null, "room"),
        room: cell(room.RoomName || null, "room"),
        template: cell(templateName(ctx, room.RoomTemplateID)),
        classification: cell(classificationLabel(ctx, room.ClassificationID)),
        areaSf: cell(dims ? areas.floorSF : null, "calculated"),
        lengthFt: cell(num(room.LengthFt), "room"),
        widthFt: cell(num(room.WidthFt), "room"),
        heightFt: cell(num(room.HeightFt), "room"),
        ach: cell(num(room.ACH), "room"),
        supplyCfm: cell(dims && +room.ACH > 0 ? sys.hvac.requiredCfm : null, "calculated"),
        pressure: cell(room.PressureTarget || null, "room"),
        ahu: cell(zoneName(ctx, room.AHUZoneID)),
        airDelivery: critCell(ctx, "mech", "air_delivery", room),
        filterType: critCell(ctx, "mech", "filter_type", room),
        indoorTemp: critNum(ctx, "mech", "indoor_design_temp", room),
        indoorRh: critNum(ctx, "mech", "indoor_design_rh", room),
        lightLevel: critCell(ctx, "elec", "light_level", room),
        fixtureType: critCell(ctx, "elec", "lighting", room),
        occupancy: critNum(ctx, "mech", "occupancy_count", room),
        exhaustCfm: critNum(ctx, "mech", "exhaust_cfm", room),
      };
    });
  },
  notes(ctx, level) {
    const out = ["Area is the room's traced outline where one exists, else length × width. Supply air = volume × ACH ÷ 60."];
    if (level === "internal" && roomCriteriaHasDefaults(ctx)) out.push("† = room template default, ‡ = project default — not a room-specific selection.");
    return out;
  },
  // Client-facing qualification only when some criterion actually comes from
  // a template / project default rather than a room-specific selection.
  qualifications: (ctx) => roomCriteriaHasDefaults(ctx) ? ["design_criteria_basis"] : [],
};
function roomCriteriaHasDefaults(ctx) {
  return sortedRooms(ctx).some(r => ["air_delivery", "filter_type", "indoor_design_temp", "indoor_design_rh", "light_level"].some(k => { const e = crit(ctx, k === "light_level" ? "elec" : "mech", k, r); return e.value !== "" && e.source !== "override"; }));
}

const hepaSchedule = {
  key: "hepa_filters",
  title: "HEPA / Terminal Filter Schedule",
  domain: "hvac",
  source: "Room HEPA engine (roomSystemsEngine.js) — required airflow from room volume × ACH, filter attributes from the selected Basis of Design option; one type row per room",
  defaultSort: "room mark",
  levels: ["internal", "proposal", "dd"],
  columns: [
    { key: "mark",        label: "#",              format: "text", align: "left" },
    { key: "room",        label: "Room",           format: "text", align: "left" },
    { key: "filterType",  label: "Filter type",    format: "text", align: "left" },
    { key: "requiredCfm", label: "Required air",   unit: "CFM",  format: "int" },
    { key: "designCfm",   label: "Design cfm/unit", unit: "CFM", format: "int" },
    { key: "faceAreaSf",  label: "Face area",      unit: "SF",   format: "num1", levels: ["internal", "dd"] },
    { key: "calculated",  label: "Calculated",     unit: "ea",   format: "int",  levels: ["internal"] },
    { key: "override",    label: "Override",       unit: "ea",   format: "int",  levels: ["internal"] },
    { key: "governing",   label: "Governs",        format: "text", levels: ["internal", "dd"] },
    { key: "effective",   label: "Qty",            unit: "ea",   format: "int" },
    { key: "actualCfm",   label: "Actual cfm/unit", unit: "CFM", format: "int",  levels: ["internal", "dd"] },
    { key: "faceVelocity", label: "Face velocity", unit: "fpm",  format: "int",  levels: ["internal", "dd"] },
    { key: "coveragePct", label: "Ceiling coverage", format: "pct", levels: ["internal", "dd"] },
    { key: "placed",      label: "On canvas",      unit: "ea",   format: "int",  levels: ["internal"] },
    { key: "warnings",    label: "Warnings",       format: "list", align: "left", levels: ["internal"] },
  ],
  rows(ctx) {
    const rows = [];
    for (const room of sortedRooms(ctx)) {
      const h = systemsFor(ctx, room).hvac.hepa;
      if (!h.enabled) continue; // no HEPA element on this room (presence is manual)
      // Ungated caller (no element map): only rooms that actually have airflow to filter.
      if (!ctx.systemsCtx?.roomElementsByRoom && !(h.requiredCfm > 0)) continue;
      const entity = hepaEntityFor(ctx, room);
      rows.push({
        id: room.RoomID,
        mark: cell(room.RoomNumber || null, "room"),
        room: cell(room.RoomName || null, "room"),
        filterType: cell(h.filterType || null, h.filterType ? critCell(ctx, "mech", "filter_type", room).source : null),
        requiredCfm: cell(h.requiredCfm > 0 ? h.requiredCfm : null, "calculated"),
        designCfm: cell(h.designAirflowPerUnit, h.designAirflowSource === "option" ? "bod" : "project"),
        faceAreaSf: cell(h.faceAreaSf, h.faceAreaSource === "option" ? "bod" : "project"),
        calculated: cell(h.calculatedQuantity, "calculated"),
        override: cell(h.overrideQuantity, h.overrideQuantity != null ? "override" : null),
        governing: cell(h.governingConstraint ? (h.governingConstraint === "coverage" ? "Ceiling coverage" : "Airflow") : null),
        effective: cell(h.effectiveQuantity, h.overrideQuantity != null ? "override" : "calculated"),
        actualCfm: cell(h.effectiveQuantity > 0 ? h.actualAirflowPerUnit : null, "calculated"),
        faceVelocity: cell(h.effectiveQuantity > 0 ? h.faceVelocityFpm : null, "calculated"),
        coveragePct: cell(h.effectiveQuantity > 0 ? h.actualCoveragePercent : null, "calculated"),
        placed: cell(entity ? placedCountFor(entity, room.RoomID, ctx.takeoffConditions || []) : null),
        warnings: cell(h.warnings.map(w => w.message)),
      });
    }
    return rows;
  },
  notes(ctx, level) {
    const out = ["One row per room: the room's selected terminal filter type and its effective count. Rooms without a HEPA element are omitted."];
    if (level === "internal") out.push("Qty = override when set (*), else calculated; Design cfm/unit and Face area come from the Basis of Design option (‡ = built-in default because the option carries none).");
    return out;
  },
  qualifications: ["hepa_estimate_stage"],
};

const lightingSchedule = {
  key: "light_fixtures",
  title: "Light Fixture Schedule",
  domain: "electrical",
  source: "Room lighting engine (roomSystemsEngine.js) — lumen method against the selected Basis of Design fixture, spacing check; one type row per room",
  defaultSort: "room mark",
  levels: ["internal", "proposal", "dd"],
  columns: [
    { key: "mark",        label: "#",               format: "text", align: "left" },
    { key: "room",        label: "Room",            format: "text", align: "left" },
    { key: "fixtureType", label: "Fixture type",    format: "text", align: "left" },
    { key: "targetFc",    label: "Target",          unit: "fc",  format: "int" },
    { key: "lumens",      label: "Lumens/fixture",  unit: "lm",  format: "int",  levels: ["internal", "dd"] },
    { key: "calculated",  label: "Calculated",      unit: "ea",  format: "int",  levels: ["internal"] },
    { key: "override",    label: "Override",        unit: "ea",  format: "int",  levels: ["internal"] },
    { key: "governing",   label: "Governs",         format: "text", levels: ["internal", "dd"] },
    { key: "effective",   label: "Qty",             unit: "ea",  format: "int" },
    { key: "watts",       label: "Connected load",  unit: "W",   format: "int" },
    { key: "va",          label: "Load",            unit: "VA",  format: "int",  levels: ["internal", "dd"] },
    { key: "maxSpacing",  label: "Max spacing",     unit: "ft",  format: "num1", levels: ["internal", "dd"] },
    { key: "placed",      label: "On canvas",       unit: "ea",  format: "int",  levels: ["internal"] },
    { key: "warnings",    label: "Warnings",        format: "list", align: "left", levels: ["internal"] },
  ],
  rows(ctx) {
    const rows = [];
    for (const room of sortedRooms(ctx)) {
      const l = systemsFor(ctx, room).electrical.lighting;
      if (!l.enabled) continue;
      if (!ctx.systemsCtx?.roomElementsByRoom && !(roomAreasOf(room).floorSF > 0)) continue;
      const entity = lightEntityFor(ctx, room);
      const qtySource = l.quantitySource === "override" ? "override" : l.quantitySource === "calculated" ? "calculated" : l.quantitySource === "template" || l.quantitySource === "project" ? l.quantitySource : null;
      rows.push({
        id: room.RoomID,
        mark: cell(room.RoomNumber || null, "room"),
        room: cell(room.RoomName || null, "room"),
        fixtureType: cell(l.fixtureType || null, l.fixtureType ? critCell(ctx, "elec", "lighting", room).source : null),
        targetFc: cell(l.targetFc ?? parseFootCandles(crit(ctx, "elec", "light_level", room).value), critCell(ctx, "elec", "light_level", room).source),
        lumens: cell(l.fixtureLumens, l.fixtureLumens ? "bod" : null),
        calculated: cell(l.calculatedQuantity, "calculated"),
        override: cell(l.overrideQuantity, l.overrideQuantity != null ? "override" : null),
        governing: cell(l.governingConstraint ? (l.governingConstraint === "spacing" ? "Spacing" : "Lumens") : l.quantitySource === "template" || l.quantitySource === "project" ? "Default count" : null),
        effective: cell(l.effectiveQuantity, qtySource),
        watts: cell(l.effectiveQuantity > 0 ? l.totalWatts : null, "calculated"),
        va: cell(l.effectiveQuantity > 0 ? l.totalVa : null, "calculated"),
        maxSpacing: cell(l.maxSpacingFt > 0 ? l.maxSpacingFt : null, "calculated"),
        placed: cell(entity ? placedCountFor(entity, room.RoomID, ctx.takeoffConditions || []) : null),
        warnings: cell(l.warnings.map(w => w.message)),
      });
    }
    return rows;
  },
  notes(ctx, level) {
    const out = ["One row per room: the room's selected fixture type and its effective count. Rooms without a lighting element are omitted."];
    if (level === "internal") out.push("Qty = override when set (*), else the lumen-method calculation, else the template (†) / project (‡) default count when the fixture option has no lumen rating.");
    return out;
  },
  qualifications: ["lighting_estimate_stage"],
};

// The room's own HEPA/light element entity (for the placed count) — the same
// resolution the flyout card uses: an element on this room whose entity is
// tagged for that system.
function elementEntityFor(ctx, room, system) {
  const els = ctx.systemsCtx?.roomElementsByRoom?.[room.RoomID] || [];
  return els.find(e => e.AutoSystem === system || e.ElementQuantityBasis === `engine_${system}`) || null;
}
const hepaEntityFor = (ctx, room) => elementEntityFor(ctx, room, "hepa");
const lightEntityFor = (ctx, room) => elementEntityFor(ctx, room, "light");

// ---- P2-D: Assumed Equipment Schedule ---------------------------------------------------
// Generated from ProcessEquipment + EquipmentConnections (db/24) — the same
// rows the Assumed Equipment page edits and the HVAC / electrical / utility
// engines read. One row per equipment RECORD (Qty is a column). Load
// summaries come from the P2-B contract (equipmentLoads.js) so the schedule
// prints exactly what the engines consume; provenance (library copy vs
// project-specific) and derived heat are Internal / DD only.

// Equipment tag sort: natural ("AUT-2" < "AUT-10"), untagged rows after tagged
// ones by DisplayOrder, then EquipmentID — same stability contract as
// compareRoomMarks.
export function compareEquipmentTags(a, b) {
  const ta = String(a.Tag ?? "").trim(), tb = String(b.Tag ?? "").trim();
  if (ta && !tb) return -1;
  if (!ta && tb) return 1;
  if (ta && tb) { const c = ta.localeCompare(tb, "en", { numeric: true, sensitivity: "base" }); if (c !== 0) return c; }
  const d = (num(a.DisplayOrder) ?? 0) - (num(b.DisplayOrder) ?? 0);
  return d !== 0 ? d : (num(a.EquipmentID) ?? 0) - (num(b.EquipmentID) ?? 0);
}

// "2 ckt · 480/120V 3/1φ · 7.7 kW" for a record's electrical connections (per unit).
export function equipmentElectricalText(eq, connections = []) {
  const rows = connections.filter(c => c.EquipmentID === eq.EquipmentID && isElectricalType(c.UtilityType));
  if (!rows.length) return null;
  const volts = [...new Set(rows.map(c => c.Volts).filter(Boolean))];
  const phases = [...new Set(rows.map(c => num(c.Phase)).filter(v => v !== null))];
  const kw = rows.reduce((t, c) => t + (num(c.KW) ?? 0), 0);
  const hp = rows.reduce((t, c) => t + (num(c.HP) ?? 0), 0);
  const load = kw ? `${r(kw, 1)} kW` : hp ? `${r(hp, 2)} HP` : null;
  return [`${rows.length} ckt`, `${volts.join("/") || "—"}V${phases.length ? ` ${phases.join("/")}φ` : ""}`, load].filter(Boolean).join(" · ");
}

// ["Compressed air (CCA) 1.2 SCFM @ 90 psig (1/2\" NPT)", ...] for a record's utility connections.
export function equipmentUtilityTexts(eq, connections = []) {
  return connections
    .filter(c => c.EquipmentID === eq.EquipmentID && !isElectricalType(c.UtilityType))
    .map(c => {
      const flow = num(c.FlowValue), pressure = num(c.PressureValue);
      const bits = [utilityLabel(c.UtilityType)];
      if (flow !== null) bits.push(`${r(flow, 2)} ${unitLabel(c.FlowUnits)}`.trim());
      if (pressure !== null) bits.push(`@ ${r(pressure, 1)} ${unitLabel(c.PressureUnits)}`.trim());
      if (c.BranchSize) bits.push(`(${c.BranchSize})`);
      return bits.join(" ");
    });
}

const assumedEquipmentSchedule = {
  key: "assumed_equipment",
  title: "Assumed Equipment Schedule",
  domain: "equipment",
  source: "ProcessEquipment + EquipmentConnections (Assumed Equipment page); room heat per equipmentLoads.js (explicit BTUH → kW × usage factor; HP alone unresolved) × active units",
  defaultSort: "equipment tag",
  levels: ["internal", "proposal", "dd"],
  columns: [
    { key: "tag",          label: "Tag",               format: "text", align: "left" },
    { key: "description",  label: "Description",       format: "text", align: "left" },
    { key: "category",     label: "Category",          format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "manufacturer", label: "Manufacturer",      format: "text", align: "left" },
    { key: "model",        label: "Model",             format: "text", align: "left" },
    { key: "dimensions",   label: "W × D × H",         format: "text", align: "left" },
    { key: "weightLbs",    label: "Weight",            unit: "lbs",  format: "int",  levels: ["internal", "dd"] },
    { key: "qty",          label: "Qty",               unit: "ea",   format: "int" },
    { key: "standby",      label: "Standby",           unit: "ea",   format: "int",  levels: ["internal", "dd"] },
    { key: "location",     label: "Room / location",   format: "text", align: "left" },
    { key: "status",       label: "Status",            format: "text" },
    // Scenarios Phase J: which design scenario claims the record as plant.
    // Proposal level lists the selected base's scope only (an alternate's
    // plant is priced and presented with that alternate).
    { key: "scenario",     label: "Design scenario",   format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "furnishedBy",  label: "Furnished by",      format: "text" },
    { key: "installedBy",  label: "Installed by",      format: "text" },
    { key: "electrical",   label: "Electrical",        format: "text", align: "left" },
    { key: "utilities",    label: "Utilities",         format: "list", align: "left" },
    { key: "heatBtuh",     label: "Heat to room",      unit: "BTUH", format: "int",  levels: ["internal", "dd"] },
    { key: "heatBasis",    label: "Heat basis",        format: "text", align: "left", levels: ["internal"] },
    // Proposal: the simplified client-facing Basis (assumed vs confirmed —
    // product decision 2026-09-12); Internal / DD: the detailed data basis.
    { key: "basis",        label: "Basis",             format: "text", levels: ["proposal"] },
    { key: "dataBasis",    label: "Data basis",        format: "text", levels: ["internal", "dd"] },
    { key: "provenance",   label: "Source",            format: "text", align: "left", levels: ["internal"] },
    { key: "notes",        label: "Notes",             format: "text", align: "left", levels: ["internal", "dd"] },
  ],
  rows(ctx, level = "internal") {
    const conns = ctx.connections || [];
    const roomsById = new Map((ctx.rooms || []).map(rm => [rm.RoomID, rm]));
    const dispositions = ctx.scenarioDispositions || null;
    const dispositionOf = (eq) => dispositions?.byEquipment?.get(eq.EquipmentID) || null;
    return [...(ctx.equipment || [])].sort(compareEquipmentTags)
      .filter(eq => level !== "proposal" || !["alternate", "excluded"].includes(dispositionOf(eq)?.disposition))
      .map(eq => {
      const room = eq.RoomID != null ? roomsById.get(eq.RoomID) : null;
      const heat = equipmentHeatGain(eq, conns);
      const heatSource = heat.effectiveBtuh <= 0 ? null : heat.method === "explicit" ? "room" : "calculated";
      const basis = heat.method === "none" ? (heat.warnings.length ? "unresolved" : null)
        : `${heat.method}${heat.activeUnits !== heat.qty ? ` × ${heat.activeUnits} active of ${heat.qty}` : ""}${heat.warnings.length ? ` · ${heat.warnings.length} warning${heat.warnings.length === 1 ? "" : "s"}` : ""}`;
      return {
        id: eq.EquipmentID,
        tag: cell(eq.Tag || null, "room"),
        description: cell(eq.Description || null, "room"),
        category: cell(eq.Category || null),
        manufacturer: cell(eq.Manufacturer || null),
        model: cell(eq.Model || null),
        dimensions: cell(dimensionsText(eq)),
        weightLbs: cell(num(eq.WeightLbs)),
        qty: cell(eq.Qty ?? null, "room"),
        standby: cell(eq.StandbyUnits || null),
        location: cell(room ? `${room.RoomNumber ? `${room.RoomNumber} — ` : ""}${room.RoomName}` : eq.LocationText || null, room ? "room" : null),
        status: cell(eq.Status ? statusLabel(eq.Status) : null),
        scenario: cell(scenarioDispositionLabel(dispositionOf(eq), dispositions)),
        furnishedBy: cell(eq.FurnishedBy ? partyLabel(eq.FurnishedBy) : null),
        installedBy: cell(eq.InstalledBy ? partyLabel(eq.InstalledBy) : null),
        electrical: cell(equipmentElectricalText(eq, conns)),
        utilities: cell(equipmentUtilityTexts(eq, conns)),
        heatBtuh: cell(heat.effectiveBtuh > 0 ? heat.effectiveBtuh : null, heatSource),
        heatBasis: cell(basis),
        basis: cell(proposalBasisLabel(eq.AssumptionStatus)),
        dataBasis: cell(assumptionLabel(eq.AssumptionStatus), eq.AssumptionStatus && eq.AssumptionStatus !== "assumed" ? "override" : null),
        provenance: cell(eq.LibraryItemID != null ? `library item #${eq.LibraryItemID}` : "project-specific"),
        notes: cell(eq.Notes || null),
      };
    });
  },
  notes(ctx, level) {
    const out = ["One row per equipment record; Qty is the number of installed units. Electrical shows circuit count, voltage/phase and connected load per unit; Utilities list each service's flow and pressure."];
    if (level !== "proposal") {
      out.push("Heat to room = Σ connections (explicit BTUH, else kW × usage factor × 3,412; HP alone is not converted — shown as unresolved) × active units (Qty − standby). Source = library copy vs. project-specific record (provenance only; never live-linked).");
      if (ctx.scenarioDispositions) out.push("Design scenario = the scenario whose HVAC system names the record as plant: the selected base's plant and unclaimed records are Base Scope; a proposal alternate's plant prices as that alternate; the plant of a draft / active-comparison / archived scenario is not priced while a base exists.");
    } else if (ctx.scenarioDispositions?.base) {
      out.push("Equipment listed is the selected base design's scope; a proposal alternate's plant is priced and listed with that alternate.");
    }
    return out;
  },
  qualifications: ["assumed_equipment"],
};

// ---- P1 Phase C: opening schedules (Door / Window / Pass-Through) -------------------------
// One row per PLACED OPENING — the TakeoffConditionsDrawn row itself is the
// design record (src/config/openings.js): Mark is the human identity, the
// row id the machine one. From = the room the symbol was placed in; To =
// the estimator's RoomBID (marked *) else the adjacent room whose wall the
// symbol sits in (derived, unmarked); an exterior opening has none.
// Sizes come from the instance (geometry width, height override) else the
// type's defaults. Nothing is renumbered here — an unmarked or duplicate
// mark is reported, never repaired.
const roomLabel = (room) => room ? `${room.RoomNumber ? `${room.RoomNumber} — ` : ""}${room.RoomName}` : null;

function openingSchedule(kind, key, title) {
  return {
    key,
    title,
    domain: "arch",
    source: `Placed ${kind.label.toLowerCase()} symbols (TakeoffConditionsDrawn, Style='count' tagged ${kind.field}); Mark = the row's own identity; To room from RoomBID else wall adjacency`,
    defaultSort: "mark",
    levels: ["internal", "proposal", "dd"],
    columns: [
      { key: "mark",     label: "Mark",        format: "text", align: "left" },
      { key: "type",     label: "Type",        format: "text", align: "left" },
      { key: "fromRoom", label: "From room",   format: "text", align: "left" },
      { key: "toRoom",   label: "To room",     format: "text", align: "left" },
      { key: "widthFt",  label: "Clear width", unit: "ft", format: "num1" },
      { key: "heightFt", label: "Height",      unit: "ft", format: "num1" },
      { key: "qty",      label: "Qty",         unit: "ea", format: "int" },
      { key: "notes",    label: "Notes",       format: "text", align: "left", levels: ["internal", "dd"] },
      { key: "recordId", label: "Record",      format: "text", align: "left", levels: ["internal"] },
      { key: "warnings", label: "Warnings",    format: "list", align: "left", levels: ["internal"] },
    ],
    rows(ctx) {
      const rooms = ctx.rooms || [];
      const all = (ctx.takeoffConditions || []).filter(c => openingKindOf(c)?.key === kind.key);
      const markCounts = new Map();
      for (const c of all) { const m = String(c.Mark ?? "").trim().toLowerCase(); if (m) markCounts.set(m, (markCounts.get(m) || 0) + 1); }
      return [...all].sort(compareOpeningMarks).map(c => {
        const { from, to, toSource } = openingRooms(c, rooms, ctx.systemsCtx?.tunables);
        const mark = String(c.Mark ?? "").trim();
        const warnings = [];
        if (!mark) warnings.push("No mark — open the symbol and give it one (suggested at placement).");
        else if (markCounts.get(mark.toLowerCase()) > 1) warnings.push(`Mark ${mark} is used ${markCounts.get(mark.toLowerCase())} times in this revision.`);
        else if (!parseMark(mark)) warnings.push(`Mark ${mark} does not follow <prefix>-<room>-<n>; kept as entered.`);
        if (!from) warnings.push("Not associated with a room.");
        if (from && !to) warnings.push("No adjacent room found — exterior opening, or set the To room on the symbol.");
        return {
          id: c.TakeoffConditionDrawnID,
          mark: cell(mark || null, "room"),
          type: cell([c.Code, c.Name].filter(Boolean).join(" — ") || null),
          fromRoom: cell(roomLabel(from), from ? "room" : null),
          toRoom: cell(roomLabel(to), toSource === "override" ? "override" : toSource === "geometry" ? "calculated" : null),
          widthFt: cell(openingWidthFt(c)),
          heightFt: cell(openingHeightFt(c), c.HeightFtOverride != null && c.HeightFtOverride !== "" ? "override" : null),
          qty: cell(c.QuantityOverride != null ? +c.QuantityOverride : 1),
          notes: cell(c.Notes || null),
          recordId: cell(`#${c.TakeoffConditionDrawnID}`),
          warnings: cell(warnings),
        };
      });
    },
    notes(ctx, level) {
      const out = [`One row per placed ${kind.label.toLowerCase()}; the mark is the opening's identity across canvas, schedules, interlocks and revisions and is never renumbered automatically.`];
      if (level !== "proposal") {
        out.push("To room: * = set by the estimator on the symbol; otherwise derived from the wall the symbol sits in (blank = exterior or not adjacent). Width = placed width else the type default; Height = the instance's override (*) else the type default.");
      }
      return out;
    },
    qualifications: ["openings_layout"],
  };
}
const doorSchedule = openingSchedule(OPENING_KINDS[0], "door_schedule", "Door Schedule");
const windowSchedule = openingSchedule(OPENING_KINDS[1], "window_schedule", "Window / Vision Panel Schedule");
const passThroughSchedule = openingSchedule(OPENING_KINDS[2], "pass_through_schedule", "Pass-Through Schedule");

// ---- P1 Phase D: Panel Schedule + Circuit Schedule ---------------------------------------
// Straight from EstimatePanels + EstimateCircuits (db/24) — the same rows the
// Electrical Estimate's Panels & Circuits grid edits and regenerates. Connected
// load / amps / spaces come from src/utils/panelLoads.js; NO demand factor,
// phase balance or mains recommendation is shown (no agreed engineering basis
// yet — Electrical Design Engine Phase E). Panel identity = PanelName (the
// estimator's own mark); circuit identity = panel + CircuitNumber.
const powerClassLabel = (v) => v ? String(v).replace(/_/g, " ") : null;
// The product-owner electrical qualification lives in scheduleQualifications.js
// (key 'electrical_panels_circuits') and is referenced by BOTH schedules below,
// so it prints once per publication.
const fedFromText = (panel, panelsById) => panel.FedFromText
  || (panel.FedFromPanelID != null ? panelsById.get(panel.FedFromPanelID)?.PanelName || null : null);

const panelSchedule = {
  key: "panel_schedule",
  title: "Panel Schedule",
  domain: "elec",
  source: "EstimatePanels + Σ EstimateCircuits.ConnectedVA per panel (Panels & Circuits grid); connected amps = VA ÷ (V_LL × √3) 3φ, VA ÷ V 1φ — no demand factors",
  defaultSort: "panel order",
  levels: ["internal", "proposal", "dd"],
  columns: [
    { key: "panel",         label: "Panel",           format: "text", align: "left" },
    { key: "status",        label: "Status",          format: "text" },
    { key: "voltage",       label: "Voltage",         format: "text", align: "left" },
    { key: "mains",         label: "Mains",           format: "text", align: "left" },
    { key: "aic",           label: "AIC",             format: "text", levels: ["internal", "dd"] },
    { key: "enclosure",     label: "Mounting / encl.", format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "location",      label: "Location",        format: "text", align: "left" },
    { key: "fedFrom",       label: "Fed from",        format: "text", align: "left" },
    { key: "feeder",        label: "Feeder",          format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "transformerKva", label: "Transformer",    unit: "kVA", format: "num1", levels: ["internal", "dd"] },
    { key: "powerClass",    label: "Power class",     format: "text", levels: ["internal", "dd"] },
    { key: "circuits",      label: "Circuits",        unit: "ea",  format: "int" },
    { key: "polesUsed",     label: "Poles used",      unit: "ea",  format: "int", levels: ["internal", "dd"] },
    { key: "spaceCount",    label: "Spaces",          unit: "ea",  format: "int", levels: ["internal", "dd"] },
    { key: "connectedKva",  label: "Connected load",  unit: "kVA", format: "num1" },
    { key: "demandKva",     label: "Demand load",     unit: "kVA", format: "num1" },
    { key: "connectedAmps", label: "Connected",       unit: "A",   format: "int", levels: ["internal", "dd"] },
    { key: "demandAmps",    label: "Demand",          unit: "A",   format: "int", levels: ["internal", "dd"] },
    { key: "demandBasis",   label: "Demand factors",  format: "list", align: "left", levels: ["internal"] },
    { key: "phaseAKva",     label: "Phase A",         unit: "kVA", format: "num1", levels: ["internal", "dd"] },
    { key: "phaseBKva",     label: "Phase B",         unit: "kVA", format: "num1", levels: ["internal", "dd"] },
    { key: "phaseCKva",     label: "Phase C",         unit: "kVA", format: "num1", levels: ["internal", "dd"] },
    { key: "imbalancePct",  label: "Imbalance",       unit: "%",   format: "num1", levels: ["internal", "dd"] },
    { key: "notes",         label: "Notes",           format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "warnings",      label: "Warnings",        format: "list", align: "left", levels: ["internal"] },
  ],
  rows(ctx) {
    const panels = [...(ctx.panels || [])].sort(comparePanels);
    const panelsById = new Map(panels.map(p => [p.PanelID, p]));
    const roomsById = new Map((ctx.rooms || []).map(r => [r.RoomID, r]));
    const demandCtx = { criteria: ctx.criteria || {}, tunables: ctx.systemsCtx?.tunables || {} };
    return panels.map(p => {
      const load = panelLoadSummary(p, ctx.circuits || [], demandCtx);
      const room = p.RoomID != null ? roomsById.get(p.RoomID) : null;
      return {
        id: p.PanelID,
        panel: cell(p.PanelName || null, "room"),
        status: cell(p.IsExisting ? "Existing" : "New"),
        voltage: cell(voltageText(p)),
        mains: cell(mainsText(p)),
        aic: cell(p.AICRating || null),
        enclosure: cell([p.Mounting, p.EnclosureType].filter(Boolean).join(" / ") || null),
        location: cell(room ? roomLabel(room) : p.LocationText || null, room ? "room" : null),
        fedFrom: cell(fedFromText(p, panelsById)),
        feeder: cell(p.FeederDesc || null),
        transformerKva: cell(num(p.TransformerKVA)),
        powerClass: cell(powerClassLabel(p.PowerClass)),
        circuits: cell(load.circuitCount, "calculated"),
        polesUsed: cell(load.polesUsed, "calculated"),
        spaceCount: cell(load.spaceCount),
        connectedKva: cell(load.connectedVA > 0 ? load.connectedVA / 1000 : null, "calculated"),
        connectedAmps: cell(load.connectedAmps !== null && load.connectedVA > 0 ? load.connectedAmps : null, "calculated"),
        demandKva: cell(load.connectedVA > 0 ? load.demandVA / 1000 : null, "calculated"),
        demandAmps: cell(load.demandAmps !== null && load.connectedVA > 0 ? load.demandAmps : null, "calculated"),
        demandBasis: cell(load.demandBasis),
        phaseAKva: cell(load.phases.assignedVA > 0 ? load.phases.byPhase.A / 1000 : null, "calculated"),
        phaseBKva: cell(load.phases.assignedVA > 0 ? load.phases.byPhase.B / 1000 : null, "calculated"),
        phaseCKva: cell(load.phases.assignedVA > 0 && +p.Phases !== 1 ? load.phases.byPhase.C / 1000 : null, "calculated"),
        imbalancePct: cell(load.phases.assignedVA > 0 ? load.phases.imbalancePct : null, "calculated"),
        notes: cell(p.Notes || null),
        warnings: cell(load.warnings.map(w => w.message)),
      };
    });
  },
  notes(ctx, level) {
    const out = ["One row per panel; Connected load is the sum of the panel's circuit VA as entered or generated. Demand load = Σ connected × each circuit's demand factor (estimator override → project design basis → CleanSpace standard → 1.0); no demand factors are applied beyond those, and no mains size is recommended — this is not code-compliant service sizing."];
    if (level !== "proposal") {
      out.push("Connected A = VA ÷ (V_LL × √3) for a 3-phase panel, VA ÷ V for single-phase, from the panel's own voltage. Poles used = Σ circuit poles against the panel's spaces. Phase A/B/C = each circuit's VA on its EXPLICIT phase assignment (a multi-pole circuit split equally across its phases; unassigned circuits excluded and warned); imbalance = (max phase − mean) ÷ mean. Demand factors column lists, per load class, the factor and its provenance (‡ project basis, * circuit override); the CleanSpace standard ships at 1.0 until an approved rule set exists.");
    }
    return out;
  },
  qualifications: ["electrical_panels_circuits"],
};

const circuitSchedule = {
  key: "circuit_schedule",
  title: "Circuit Schedule",
  domain: "elec",
  source: "EstimateCircuits joined to its panel, room and equipment connection (Panels & Circuits grid / ⚡ Regenerate Circuits)",
  defaultSort: "panel, circuit number",
  levels: ["internal", "proposal", "dd"],
  columns: [
    { key: "panel",       label: "Panel",        format: "text", align: "left" },
    { key: "circuit",     label: "Ckt",          format: "int" },
    { key: "phase",       label: "Phase",        format: "text" },
    { key: "poles",       label: "Poles",        format: "int" },
    { key: "trip",        label: "Trip",         unit: "A",  format: "int" },
    { key: "description", label: "Description",  format: "text", align: "left" },
    { key: "loadClass",   label: "Load class",   format: "text" },
    { key: "room",        label: "Room",         format: "text", align: "left" },
    { key: "equipment",   label: "Equipment",    format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "connectedVa", label: "Connected",    unit: "VA", format: "int", levels: ["internal", "dd"] },
    { key: "demandFactor", label: "DF",          format: "num2", levels: ["internal", "dd"] },
    { key: "demandVa",    label: "Demand",       unit: "VA", format: "int", levels: ["internal", "dd"] },
    { key: "conductors",  label: "Conduit / conductors", format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "routeLf",     label: "Route",        unit: "LF", format: "num1", levels: ["internal"] },
    { key: "notes",       label: "Notes",        format: "text", align: "left", levels: ["internal"] },
    { key: "warnings",    label: "Warnings",     format: "list", align: "left", levels: ["internal"] },
  ],
  rows(ctx) {
    const panels = [...(ctx.panels || [])].sort(comparePanels);
    const panelOrder = new Map(panels.map((p, i) => [p.PanelID, i]));
    const panelsById = new Map(panels.map(p => [p.PanelID, p]));
    const roomsById = new Map((ctx.rooms || []).map(r => [r.RoomID, r]));
    const connById = new Map((ctx.connections || []).map(c => [c.ConnectionID, c]));
    const equipById = new Map((ctx.equipment || []).map(e => [e.EquipmentID, e]));
    const circuits = (ctx.circuits || []).filter(c => panelsById.has(c.PanelID));
    const demandCtx = { criteria: ctx.criteria || {}, tunables: ctx.systemsCtx?.tunables || {} };
    const numberCounts = new Map();
    for (const c of circuits) { const k = `${c.PanelID}:${c.CircuitNumber}`; numberCounts.set(k, (numberCounts.get(k) || 0) + 1); }
    return [...circuits]
      .sort((a, b) => (panelOrder.get(a.PanelID) - panelOrder.get(b.PanelID)) || ((num(a.CircuitNumber) ?? 0) - (num(b.CircuitNumber) ?? 0)) || ((num(a.CircuitID) ?? 0) - (num(b.CircuitID) ?? 0)))
      .map(c => {
        const panel = panelsById.get(c.PanelID);
        const room = c.RoomID != null ? roomsById.get(c.RoomID) : null;
        const conn = c.ConnectionID != null ? connById.get(c.ConnectionID) : null;
        const equip = conn ? equipById.get(conn.EquipmentID) : null;
        const spare = c.LoadClass === "spare" || c.TripAmps == null;
        const va = num(c.ConnectedVA) ?? 0;
        const warnings = [];
        if (numberCounts.get(`${c.PanelID}:${c.CircuitNumber}`) > 1) warnings.push(`Circuit number ${c.CircuitNumber} is used more than once on ${panel.PanelName}.`);
        if (!spare && va <= 0) warnings.push("No connected VA.");
        if (c.ConnectionID != null && !conn) warnings.push("Equipment connection no longer exists.");
        if (c.RoomID != null && !room) warnings.push("Room no longer exists in this revision.");
        if (!spare && !phasesOf(c.Phase).length) warnings.push("No phase assignment.");
        else if (c.Phase && !isValidPhase(c.Phase, panel)) warnings.push(`Phase ${c.Phase} is not available on a ${panel.Phases}-phase panel.`);
        return {
          id: c.CircuitID,
          panel: cell(panel.PanelName || null, "room"),
          circuit: cell(num(c.CircuitNumber), "room"),
          phase: cell(phasesOf(c.Phase).length ? String(c.Phase).toUpperCase() : null, "room"),
          poles: cell(num(c.Poles) ?? 1),
          trip: cell(spare && c.TripAmps == null ? null : num(c.TripAmps)),
          description: cell(c.CircuitDesc || (spare ? "SPARE" : null)),
          loadClass: cell(c.LoadClass || null),
          room: cell(room ? roomLabel(room) : null, room ? "room" : null),
          equipment: cell(equip ? [equip.Tag, equip.Description].filter(Boolean).join(" — ") : conn ? `connection #${conn.ConnectionID}` : null),
          connectedVa: cell(va > 0 ? va : null),
          demandFactor: (() => { const df = resolveDemandFactor(c, demandCtx); return spare ? cell(null) : cell(df.factor, df.source === "override" ? "override" : df.source === "project" ? "project" : "calculated"); })(),
          demandVa: cell(va > 0 ? va * resolveDemandFactor(c, demandCtx).factor : null, "calculated"),
          conductors: cell(c.ConduitConductors || null),
          routeLf: cell(num(c.RouteLF), c.RouteLF != null ? "calculated" : null),
          notes: cell(c.Notes || null),
          warnings: cell(warnings),
        };
      });
  },
  notes(ctx, level) {
    const out = ["One row per circuit; the panel name + circuit number is the circuit's identity and its phase is the explicit stored assignment (proposed at generation, editable, never derived from the number)."];
    if (level !== "proposal") {
      out.push("Connected VA is as entered or generated (devices × VA per device, or the equipment connection's kW); route LF is the conceptual homerun + device run from the canvas. Generated and hand-edited circuits are not distinguished — the rows carry no generated/manual marker yet (Electrical Design Engine Phase D/F).");
    }
    return out;
  },
  qualifications: ["electrical_panels_circuits"],
};

// ---- Process Utilities Phase F: Process Utility Schedule + Equipment Utility Matrix -----------
// Both read the SAME rollup the Utility demand view shows (utilityDemand.js):
// EquipmentConnections → utility system → connected (active units) →
// diversity (provenance) → design demand → capacity margin → required
// capacity → redundancy → plant requirement, with the revision's
// UtilitySystems design records. Nothing is stored for the schedule.
function utilityRollup(ctx) {
  return rollupUtilityDemand(ctx.equipment || [], ctx.connections || [], {
    rooms: ctx.rooms || [], criteria: ctx.criteria || {},
    ctx: { roomElementsByRoom: ctx.systemsCtx?.roomElementsByRoom, takeoffConditions: ctx.takeoffConditions || [], formulas: ctx.systemsCtx?.formulas },
    systems: ctx.utilitySystems || [], tunables: ctx.systemsCtx?.tunables || {},
  });
}
const qty = (v, unit, d = 1) => (v === null || v === undefined || !Number.isFinite(+v)) ? null : `${r(v, d)} ${unitLabel(unit) || unit || ""}`.trim();
const pct = (v) => `${Math.round(v * 100)} %`;
const sourceMark = (source) => source === "override" ? "override" : source === "project" ? "project" : source === "scenario" || source === "standard" ? "calculated" : null;

const processUtilitySchedule = {
  key: "process_utility_schedule",
  title: "Process Utility Schedule",
  domain: "process",
  source: "UtilitySystems design records + EquipmentConnections (Utility demand view rollup): connected (active units) × diversity = design demand; × (1 + margin) = required capacity; redundancy sets the installed configuration",
  defaultSort: "utility vocabulary order",
  levels: ["internal", "proposal", "dd"],
  columns: [
    { key: "utility",          label: "Utility",            format: "text", align: "left" },
    { key: "system",           label: "System",             format: "text", align: "left" },
    { key: "equipmentCount",   label: "Equipment",          unit: "ea", format: "int", levels: ["internal", "dd"] },
    { key: "connectionCount",  label: "Connections",        unit: "ea", format: "int", levels: ["internal", "dd"] },
    { key: "installedDemand",  label: "Installed (all units)", format: "text", levels: ["internal", "dd"] },
    { key: "connectedDemand",  label: "Connected demand",   format: "text" },
    { key: "diversity",        label: "Diversity",          format: "num2" },
    { key: "diversitySource",  label: "Diversity basis",    format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "designDemand",     label: "Design demand",      format: "text" },
    { key: "capacityMargin",   label: "Margin",             format: "text", levels: ["internal", "dd"] },
    { key: "requiredCapacity", label: "Required capacity",  format: "text" },
    { key: "redundancy",       label: "Redundancy",         format: "text", align: "left" },
    { key: "installedCapacity", label: "Installed capacity", format: "text" },
    { key: "requiredPressure", label: "Required pressure",  format: "text" },
    { key: "consumption",      label: "Consumption / day",  format: "text", levels: ["internal", "dd"] },
    { key: "plantAsset",       label: "Plant asset",        format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "notes",            label: "Notes",              format: "text", align: "left", levels: ["internal", "dd"] },
    { key: "warnings",         label: "Warnings",           format: "list", align: "left", levels: ["internal"] },
  ],
  rows(ctx) {
    const equipById = new Map((ctx.equipment || []).map(e => [e.EquipmentID, e]));
    return utilityRollup(ctx).systems.map(s => {
      const u = s.governingUnits;
      const red = s.redundancy;
      const asset = s.record?.SourceEquipmentID != null ? equipById.get(s.record.SourceEquipmentID) : null;
      const redundancyText = red.strategy === "none" ? "None"
        : `${red.label}${red.configured ? ` · ${red.unitCount ?? "?"} × ${qty(red.unitCapacity, s.capacityUnits || u)}, ${red.dutyUnits ?? "?"} duty / ${red.standbyUnits ?? "?"} standby` : " (not configured)"}${red.note ? ` — ${red.note}` : ""}`;
      return {
        id: s.utilityType,
        utility: cell(s.label, "room"),
        system: cell(s.record?.SystemName || null),
        equipmentCount: cell(s.equipmentCount, "calculated"),
        connectionCount: cell(s.connectionCount, "calculated"),
        installedDemand: cell(s.connectionCount ? qty(s.installedDemand, u) : null, "calculated"),
        connectedDemand: cell(s.connectionCount ? qty(s.connectedDemand, u) : null, s.record?.IncludeStandbyInConnected ? "project" : "calculated"),
        diversity: cell(s.diversity.value, sourceMark(s.diversity.source)),
        diversitySource: cell(s.diversity.label),
        designDemand: cell(s.connectionCount ? qty(s.designDemand, u) : null, "calculated"),
        capacityMargin: cell(`${pct(s.capacityMargin.value)} (${s.capacityMargin.label})`, sourceMark(s.capacityMargin.source)),
        requiredCapacity: cell(s.connectionCount ? qty(s.requiredCapacity, u) : null, "calculated"),
        redundancy: cell(redundancyText, s.record ? "project" : null),
        installedCapacity: cell(s.installedCapacity != null ? qty(s.installedCapacity, s.capacityUnits || u) : null, s.record ? "project" : null),
        requiredPressure: cell(s.governingPressure ? qty(s.governingPressure.value, s.governingPressure.units) : null, "calculated"),
        consumption: cell(s.consumptionPerDay ? qty(s.consumptionPerDay.value, s.consumptionPerDay.units) : null, "calculated"),
        plantAsset: cell(asset ? [asset.Tag, asset.Description].filter(Boolean).join(" — ") : null),
        notes: cell(s.record?.Notes || null),
        warnings: cell(s.warnings.map(w => w.message)),
      };
    });
  },
  notes(ctx, level) {
    const out = ["One row per process utility system. Connected demand counts each connection's per-unit flow at the equipment's active units (installed − standby); design demand = connected × diversity factor; required capacity = design demand × (1 + capacity margin); the redundancy strategy sets the installed configuration and never reduces the requirement. Required pressure is the highest connection requirement."];
    if (level !== "proposal") out.push("Diversity and margin carry their provenance (‡ project design basis, * estimator override; CleanSpace standard and the connections' operating scenario are calculated). A utility with no system record shows the standard / default values (1.00, 0 %). Installed (all units) is the pipe/branch sizing context only.");
    return out;
  },
  qualifications: ["process_utilities_estimate_stage"],
};

// Equipment Utility Matrix — one row per equipment record, one column per
// utility type present in the revision's connections (electrical included),
// each cell the per-unit connection(s) of that type; a final row carries the
// system connected demand from the same rollup so the matrix and the Process
// Utility Schedule can never disagree.
const utilityTypesPresent = (ctx) => Object.keys(UTILITY_BY_KEY).filter(t => (ctx.connections || []).some(c => c.UtilityType === t));
const utilityColKey = (t) => `u_${t}`;
function matrixCellText(eq, type, connections) {
  const mine = connections.filter(c => c.EquipmentID === eq.EquipmentID && c.UtilityType === type);
  if (!mine.length) return null;
  if (isElectricalType(type)) return equipmentElectricalText(eq, connections);
  return mine.map(c => {
    const flow = num(c.FlowValue), pressure = num(c.PressureValue);
    const bits = [];
    if (flow !== null) bits.push(`${r(flow, 2)} ${unitLabel(c.FlowUnits)}`.trim());
    if (pressure !== null) bits.push(`@ ${r(pressure, 1)} ${unitLabel(c.PressureUnits)}`.trim());
    if (c.BranchSize) bits.push(`(${c.BranchSize})`);
    if (c.CircuitDesc) bits.push(`— ${c.CircuitDesc}`);
    return bits.join(" ") || "connection";
  }).join("; ");
}
const equipmentUtilityMatrix = {
  key: "equipment_utility_matrix",
  title: "Equipment Utility Matrix",
  domain: "process",
  source: "ProcessEquipment × EquipmentConnections — per-unit connection requirements by utility; the last row is each utility system's connected demand from the Process Utility Schedule rollup",
  defaultSort: "equipment tag",
  levels: ["internal", "proposal", "dd"],
  columns: (ctx) => [
    { key: "tag",         label: "Tag",             format: "text", align: "left" },
    { key: "description", label: "Description",     format: "text", align: "left" },
    { key: "location",    label: "Room / location", format: "text", align: "left" },
    { key: "qty",         label: "Qty",             unit: "ea", format: "int" },
    { key: "standby",     label: "Standby",         unit: "ea", format: "int", levels: ["internal", "dd"] },
    ...utilityTypesPresent(ctx).map(t => ({ key: utilityColKey(t), label: utilityLabel(t), format: "text", align: "left", utilityType: t })),
  ],
  rows(ctx) {
    const conns = ctx.connections || [];
    const types = utilityTypesPresent(ctx);
    const roomsById = new Map((ctx.rooms || []).map(rm => [rm.RoomID, rm]));
    const rows = [...(ctx.equipment || [])].sort(compareEquipmentTags).map(eq => {
      const room = eq.RoomID != null ? roomsById.get(eq.RoomID) : null;
      const row = {
        id: eq.EquipmentID,
        tag: cell(eq.Tag || null, "room"),
        description: cell(eq.Description || null, "room"),
        location: cell(room ? roomLabel(room) : eq.LocationText || null, room ? "room" : null),
        qty: cell(eq.Qty ?? null, "room"),
        standby: cell(eq.StandbyUnits || null),
      };
      for (const t of types) row[utilityColKey(t)] = cell(matrixCellText(eq, t, conns));
      return row;
    });
    if (rows.length && types.some(t => !isElectricalType(t))) {
      const systems = utilityRollup(ctx).systems;
      const total = { id: "__connected_demand", tag: cell("Connected demand", "calculated"), description: cell("system total at active units, per utility"), location: cell(null), qty: cell(null), standby: cell(null) };
      for (const t of types) {
        const sys = systems.find(x => x.utilityType === t);
        total[utilityColKey(t)] = cell(!isElectricalType(t) && sys?.connectionCount ? qty(sys.connectedDemand, sys.governingUnits) : null, "calculated");
      }
      rows.push(total);
    }
    return rows;
  },
  notes(ctx, level) {
    const out = ["One row per equipment record; each utility column lists that equipment's per-unit connection requirement(s) — flow @ pressure (branch size) — as entered on the Assumed Equipment page; electrical shows circuit count, voltage/phase and connected load per unit."];
    if (level !== "proposal") out.push("The last row is each utility system's connected demand (per-unit flow × active units, converted to the system's governing units) from the same rollup as the Process Utility Schedule; rows whose units cannot be converted are excluded there and listed on that schedule's warnings.");
    return out;
  },
  qualifications: ["process_utilities_estimate_stage"],
};

// ---- HVAC Design Alternates (System Architecture & Scenarios Phase J-3) -------------------
// The proposal-facing presentation of the design scenarios: the selected base
// (the estimate's Base Scope) first, then every published proposal alternate —
// what it is (systems, architectures, zones served, plant) and what it changes
// against the base (plant added / credited). No pricing here: each alternate's
// add / deduct is carried on the Estimate under its alternate header. At the
// Internal / DD levels unpublished scenarios are listed for reference.
// ctx.scenarioModel (buildScenarioModel), ctx.scenarioDispositions
// (scenarioEquipmentDispositions with the estimate's alternate headers), ctx.zones, ctx.equipment.
const hvacDesignAlternatesSchedule = {
  key: "hvac_design_alternates",
  title: "HVAC Design Alternates",
  domain: "hvac",
  source: "DesignScenarios + HvacSystems + HvacSystemZones (Design scenarios panel) with the estimate's alternate headers; each alternate's add / deduct is priced on the Estimate",
  defaultSort: "selected base, then proposal alternates, then (Internal / DD) other scenarios in panel order",
  levels: ["internal", "proposal", "dd"],
  columns: [
    { key: "alternate",    label: "Alternate",          format: "text", align: "left" },
    { key: "scenario",     label: "Scenario",           format: "text", levels: ["internal", "dd"] },
    { key: "design",       label: "Design",             format: "list", align: "left" },
    { key: "zones",        label: "AHU zones served",   format: "list", align: "left" },
    { key: "plant",        label: "Plant",              format: "list", align: "left" },
    { key: "change",       label: "Change vs base",     format: "list", align: "left" },
    { key: "distribution", label: "Distribution",       format: "list", align: "left", levels: ["internal", "dd"] },
    { key: "status",       label: "Status",             format: "text", levels: ["internal", "dd"] },
    { key: "notes",        label: "Comparison notes",   format: "text", align: "left", levels: ["internal"] },
  ],
  rows(ctx, level = "internal") {
    const model = ctx.scenarioModel || [];
    const d = ctx.scenarioDispositions || null;
    const zonesById = new Map((ctx.zones || []).map(z => [z.AHUZoneID, z]));
    const equipById = new Map((ctx.equipment || []).map(e => [e.EquipmentID, e]));
    const tagOf = (id) => { const e = equipById.get(id); return e ? (e.Tag || e.Description || `#${id}`) : `#${id}`; };
    const zoneName = (id) => zonesById.get(id)?.ZoneName || `zone #${id}`;
    const base = d?.base || model.find(sc => sc.Status === "selected_base") || null;
    const altByScenario = new Map((d?.alternates || []).map(a => [a.scenarioId, a]));
    const ordered = [
      ...(base ? [base] : []),
      ...model.filter(sc => sc.Status === "proposal_alternate"),
      ...(level === "proposal" ? [] : model.filter(sc => sc.Status !== "selected_base" && sc.Status !== "proposal_alternate")),
    ];
    return ordered.map(sc => {
      const isBase = base && sc.ScenarioID === base.ScenarioID;
      const alt = altByScenario.get(sc.ScenarioID) || null;
      const systems = sc.systems || [];
      const plantIds = [...new Set(systems.map(h => h.PlantEquipmentID).filter(id => id != null))];
      const change = isBase ? ["Base design"] : [
        ...(alt?.addedTags?.length ? [`Adds ${alt.addedTags.join(", ")}`] : []),
        ...(alt?.creditedTags?.length ? [`Credits ${alt.creditedTags.join(", ")}`] : []),
      ];
      if (!isBase && !change.length) change.push(alt ? "Same plant as the base" : (base ? "Not published as an alternate" : "No base design selected"));
      return {
        id: sc.ScenarioID,
        alternate: cell(isBase ? "Base design" : (alt?.alternateName || `${sc.ScenarioKey} — ${sc.Title || ""}`.trim()), isBase || alt ? "room" : null),
        scenario: cell(`${sc.ScenarioKey}${sc.Title ? ` — ${sc.Title}` : ""}`),
        design: cell(systems.map(h => `${h.SystemKey} — ${architectureLabel(h.ArchitectureType)} (${h.zoneIds.length} zone${h.zoneIds.length === 1 ? "" : "s"})`)),
        zones: cell([...new Set(systems.flatMap(h => h.zoneIds))].map(zoneName)),
        plant: cell(plantIds.map(tagOf)),
        change: cell(change),
        distribution: cell(systems.filter(h => h.DistributionType).map(h => `${h.SystemKey}: ${distributionTypeLabel(h.DistributionType)}`)),
        status: cell(scenarioStatusLabel(sc.Status)),
        notes: cell(sc.ComparisonNotes || null),
      };
    });
  },
  notes(ctx, level) {
    const out = ["The base design is the estimate's Base Scope. Each alternate is carried on the estimate as an add / deduct alternate: its plant and distribution added, the base's replaced plant and distribution credited."];
    if (level !== "proposal") out.push("Scenarios in draft, active comparison or archived are listed for reference only — they are not in the estimate until selected as base or published as a proposal alternate.");
    return out;
  },
  qualifications: ["hvac_design_alternates"],
};

export const DESIGN_SCHEDULES = [roomCriteriaSchedule, hepaSchedule, lightingSchedule, assumedEquipmentSchedule, doorSchedule, windowSchedule, passThroughSchedule, panelSchedule, circuitSchedule, processUtilitySchedule, equipmentUtilityMatrix, hvacDesignAlternatesSchedule];
export const SCHEDULE_BY_KEY = Object.fromEntries(DESIGN_SCHEDULES.map(s => [s.key, s]));

// ---- framework -----------------------------------------------------------------------------

export function isPublishedAt(entry, level) {
  return !entry.levels || entry.levels.includes(level);
}
export const columnsOf = (def, ctx = {}) => (typeof def.columns === "function" ? def.columns(ctx || {}) : def.columns);
export function columnsForLevel(def, level, ctx = {}) {
  return columnsOf(def, ctx).filter(c => isPublishedAt(c, level));
}
export function schedulesForLevel(level) {
  return DESIGN_SCHEDULES.filter(s => isPublishedAt(s, level));
}

// Build one schedule for a publication level: the columns published at that
// level, the rows (every cell as { value, source }), display strings per
// cell, and the qualifier notes. Rows come back in the schedule's stable
// default order; the renderer never re-sorts.
export function buildSchedule(def, ctx, level = "internal") {
  if (!PUBLICATION_LEVEL_KEYS.includes(level)) throw new Error(`Unknown publication level "${level}"`);
  const columns = columnsForLevel(def, level, ctx);
  const rows = def.rows(ctx || {}, level).map(r => {
    const cells = {};
    for (const col of columns) {
      const c = r[col.key] ?? cell(null);
      const mark = level === "internal" && c.source ? (PROVENANCE_MARKS[c.source]?.mark || "") : "";
      cells[col.key] = { ...c, text: formatCell(col, c.value), mark, sourceLabel: c.source ? PROVENANCE_MARKS[c.source]?.label || c.source : null };
    }
    return { id: r.id, cells };
  });
  const qualKeys = typeof def.qualifications === "function" ? def.qualifications(ctx || {}, level) : (def.qualifications || []);
  return {
    key: def.key, title: def.title, source: def.source, domain: def.domain, level, columns, rows,
    notes: def.notes ? def.notes(ctx || {}, level) : [],
    // Client-facing qualifications this schedule publishes at this level
    // (scheduleQualifications.js) — printed once per publication by the view;
    // an empty schedule qualifies nothing.
    qualifications: rows.length ? qualificationsFor(qualKeys, level) : [],
  };
}
// Every qualification key any schedule can reference (for the registry test).
export function scheduleQualificationKeys(def) {
  return typeof def.qualifications === "function" ? [] : (def.qualifications || []);
}

// Column header text: label plus unit ("Supply air (CFM)").
export const columnHeader = (col) => col.unit ? `${col.label} (${col.unit})` : col.label;
